import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ChallengeSendSummary from './ChallengeSendSummary';
import ChallengeTemplateConfigurator from './ChallengeTemplateConfigurator';
import {
	addDaysToDateInput,
	buildChallengePayloadFromSuggestion,
	buildChallengeTitle,
	isCustomTemplate,
	MAX_CHALLENGE_DURATION_DAYS,
	clampChallengeDurationDays,
	clampChallengeEndDate,
	daysBetweenInclusive,
	durationDaysForTemplate,
	findTemplate,
	todayDateInput
} from '../../constants/fitnessChallengeTemplates';
import {
	createFriendChallenge,
	createHouseholdChallenge,
	fetchFriendSuggestions,
	fetchHouseholdLinks,
	fetchLinkedHouseholdProfiles,
	fetchProfileSuggestions
} from '../../utils/challengeWireApi';
import { fetchUserFitnessProfiles } from '../../utils/fitnessProfilesApi';
import {
	fieldLabelClass,
	hintClass,
	inputClass,
	suggestionChipClass,
	suggestionChipReasonClass,
	suggestionChipTitleClass
} from './challengeWireFormStyles';

const DEFAULT_TEMPLATE = findTemplate('custom');

function parseRecipientKey(key) {
	if (!key || !key.includes(':')) return { type: null, id: null };
	const [type, id] = key.split(':');
	return { type, id };
}

function buildRecipientKey(type, id) {
	return id ? `${type}:${id}` : '';
}

function filterRecipientOptions(options, excludedKeys, keepKey = '') {
	const exclude = new Set((excludedKeys || []).filter((key) => key && key !== keepKey));
	const keep = (opts) => (opts || []).filter((opt) => !exclude.has(opt.key));
	const selfOpt =
		options.selfOpt && !exclude.has(options.selfOpt.key) ? options.selfOpt : null;
	const friendOpts = keep(options.friendOpts);
	const profileOpts = keep(options.profileOpts);
	const linkedProfileOpts = keep(options.linkedProfileOpts);

	return {
		selfOpt,
		friendOpts,
		profileOpts,
		linkedProfileOpts,
		hasSelf: Boolean(selfOpt),
		hasFriends: friendOpts.length > 0,
		hasProfiles: profileOpts.length > 0,
		hasLinkedProfiles: linkedProfileOpts.length > 0
	};
}

function RecipientSelect({
	id,
	value,
	onChange,
	options,
	placeholder,
	required = false,
	selectClassName = `${inputClass} mt-1`
}) {
	return (
		<select
			id={id}
			className={selectClassName}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			required={required}
		>
			<option value="">{placeholder}</option>
			{options.hasSelf && (
				<optgroup label="Yourself">
					<option key={options.selfOpt.key} value={options.selfOpt.key}>
						{options.selfOpt.label}
					</option>
				</optgroup>
			)}
			{options.hasFriends && (
				<optgroup label="Friends">
					{options.friendOpts.map((opt) => (
						<option key={opt.key} value={opt.key}>
							{opt.label}
						</option>
					))}
				</optgroup>
			)}
			{options.hasLinkedProfiles && (
				<optgroup label="Linked Households">
					{options.linkedProfileOpts.map((opt) => (
						<option key={opt.key} value={opt.key}>
							{opt.label}
						</option>
					))}
				</optgroup>
			)}
			{options.hasProfiles && (
				<optgroup label="Household profiles">
					{options.profileOpts.map((opt) => (
						<option key={opt.key} value={opt.key}>
							{opt.label}
						</option>
					))}
				</optgroup>
			)}
		</select>
	);
}

function initialSchedule() {
	const startsOn = todayDateInput();
	return {
		startsOn,
		endsOn: addDaysToDateInput(startsOn, DEFAULT_TEMPLATE.durationDays)
	};
}

export default function SendChallengeSheet({
	isOpen,
	onClose,
	user,
	csrfToken,
	endpoints,
	friends = [],
	initialRecipientKey = '',
	onSent
}) {
	const { activeFitnessProfile } = useAuth();
	const modalRef = useRef(null);

	const [recipientKey, setRecipientKey] = useState('');
	const [additionalRecipientKeys, setAdditionalRecipientKeys] = useState([]);
	const [profiles, setProfiles] = useState([]);
	const [linkedProfiles, setLinkedProfiles] = useState([]);
	const [suggestions, setSuggestions] = useState([]);
	const [suggestionMeta, setSuggestionMeta] = useState({ profile_name: '', has_profile: true });
	const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE.key);
	const [customParams, setCustomParams] = useState({ ...DEFAULT_TEMPLATE.defaultParams });
	const [startsOn, setStartsOn] = useState(() => initialSchedule().startsOn);
	const [endsOn, setEndsOn] = useState(() => initialSchedule().endsOn);
	const [previewTitle, setPreviewTitle] = useState('');
	const [titleLocked, setTitleLocked] = useState(true);
	const [personalNote, setPersonalNote] = useState('');
	const [joinAsWell, setJoinAsWell] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const { type: recipientType, id: recipientId } = parseRecipientKey(recipientKey);
	const isFriend = recipientType === 'friend';
	const isHousehold = recipientType === 'profile';
	const toProfileId = isHousehold ? recipientId : '';

	const loggedInIdentity = useMemo(() => {
		if (activeFitnessProfile?.id) {
			return {
				displayName: activeFitnessProfile.name,
				profileId: String(activeFitnessProfile.id),
				excludeProfileId: String(activeFitnessProfile.id)
			};
		}

		const primaryProfile = (profiles || []).find((p) => p.is_primary);
		return {
			displayName: user?.full_name || user?.first_name || 'Yourself',
			profileId: primaryProfile?.id != null ? String(primaryProfile.id) : null,
			excludeProfileId: primaryProfile?.id != null ? String(primaryProfile.id) : null
		};
	}, [activeFitnessProfile, profiles, user]);

	const isSelfChallenge =
		(isFriend && String(recipientId) === String(user?.id)) ||
		(isHousehold &&
			loggedInIdentity.profileId &&
			toProfileId &&
			String(toProfileId) === String(loggedInIdentity.profileId));

	const recipientOptions = useMemo(() => {
		const householdSelfOpt =
			activeFitnessProfile?.id != null
				? {
						key: buildRecipientKey('profile', activeFitnessProfile.id),
						label: loggedInIdentity.displayName,
						group: 'Yourself'
					}
				: null;
		const accountSelfOpt =
			user?.id != null && !activeFitnessProfile?.id
				? {
						key: buildRecipientKey('friend', user.id),
						label: loggedInIdentity.displayName,
						group: 'Yourself'
					}
				: null;
		const selfOpt = householdSelfOpt || accountSelfOpt;
		const friendOpts = activeFitnessProfile
			? []
			: (friends || []).map((f) => ({
					key: buildRecipientKey('friend', f.user.id),
					label: f.user.full_name,
					group: 'Friends'
				}));
		const profileOpts = (profiles || [])
			.filter(
				(p) =>
					loggedInIdentity.excludeProfileId == null ||
					String(p.id) !== loggedInIdentity.excludeProfileId
			)
			.map((p) => ({
				key: buildRecipientKey('profile', p.id),
				label: p.name,
				group: 'Household profiles'
			}));
		const linkedProfileOpts = (linkedProfiles || []).map((p) => ({
			key: buildRecipientKey('profile', p.id),
			label: `${p.name} (${p.linked_user_name || 'linked household'})`,
			group: 'Linked Households'
		}));
		return {
			selfOpt,
			friendOpts,
			profileOpts,
			linkedProfileOpts,
			hasSelf: Boolean(selfOpt),
			hasFriends: friendOpts.length > 0,
			hasProfiles: profileOpts.length > 0,
			hasLinkedProfiles: linkedProfileOpts.length > 0
		};
	}, [friends, profiles, linkedProfiles, user, loggedInIdentity, activeFitnessProfile]);

	const flattenedRecipientOptions = useMemo(() => {
		const { selfOpt, friendOpts, profileOpts, linkedProfileOpts } = recipientOptions;
		return [
			...(selfOpt ? [selfOpt] : []),
			...friendOpts,
			...linkedProfileOpts,
			...profileOpts
		];
	}, [recipientOptions]);

	const selectedRecipientKeys = useMemo(
		() => [recipientKey, ...additionalRecipientKeys].filter(Boolean),
		[recipientKey, additionalRecipientKeys]
	);

	function isSelfRecipientKey(key) {
		const { type, id } = parseRecipientKey(key);
		if (type === 'friend') return String(id) === String(user?.id);
		if (type === 'profile') {
			return (
				Boolean(loggedInIdentity.profileId) &&
				String(id) === String(loggedInIdentity.profileId)
			);
		}
		return false;
	}

	const includesSelfRecipient = selectedRecipientKeys.some(isSelfRecipientKey);

	const suggestContextLabel = useMemo(() => {
		if (isSelfChallenge) {
			return loggedInIdentity.displayName;
		}
		if (isFriend) {
			const f = friends.find((row) => String(row.user.id) === String(recipientId));
			return f?.user?.first_name || f?.user?.full_name;
		}
		if (isHousehold) {
			return profiles.find((p) => String(p.id) === String(toProfileId))?.name
				|| linkedProfiles.find((p) => String(p.id) === String(toProfileId))?.name;
		}
		return null;
	}, [isSelfChallenge, isFriend, isHousehold, recipientId, toProfileId, friends, profiles, linkedProfiles, loggedInIdentity]);

	const suggestName = suggestionMeta.profile_name || suggestContextLabel || 'them';

	const durationDays = useMemo(() => {
		if (startsOn && endsOn) return daysBetweenInclusive(startsOn, endsOn);
		return durationDaysForTemplate(findTemplate(templateKey), customParams);
	}, [startsOn, endsOn, templateKey, customParams]);

	const summaryParties = useMemo(() => {
		function labelForRecipientKey(key) {
			const option = flattenedRecipientOptions.find((opt) => opt.key === key);
			if (option?.label) return option.label;

			const { type, id } = parseRecipientKey(key);
			if (type === 'friend') {
				const friend = friends.find((row) => String(row.user.id) === String(id));
				return friend?.user?.first_name || friend?.user?.full_name;
			}
			if (type === 'profile') {
				return (
					profiles.find((p) => String(p.id) === String(id))?.name ||
					linkedProfiles.find((p) => String(p.id) === String(id))?.name ||
					(String(id) === String(loggedInIdentity.profileId)
						? loggedInIdentity.displayName
						: null)
				);
			}
			return null;
		}

		const selectedPeople = selectedRecipientKeys.map((key) => ({
			key,
			label: labelForRecipientKey(key),
			isSelf: isSelfRecipientKey(key)
		}));
		const challengedPeople = selectedPeople.filter((person) => !person.isSelf);
		const challengedNames = challengedPeople
			.map((person) => person.label)
			.filter(Boolean);

		if (selectedPeople.length === 0) return null;

		if (challengedNames.length === 0) {
			return {
				challenger: loggedInIdentity.displayName,
				challenged: loggedInIdentity.displayName,
				challengedNames: [loggedInIdentity.displayName].filter(Boolean),
				isSelfChallenge: true,
				alsoJoin: false
			};
		}

		return {
			challenger: loggedInIdentity.displayName,
			challenged: challengedNames[0],
			challengedNames,
			isSelfChallenge: false,
			alsoJoin: (joinAsWell && !includesSelfRecipient) || includesSelfRecipient
		};
	}, [
		selectedRecipientKeys,
		flattenedRecipientOptions,
		loggedInIdentity,
		joinAsWell,
		includesSelfRecipient,
		friends,
		profiles,
		linkedProfiles,
		user
	]);

	function resetForm() {
		const schedule = initialSchedule();
		setRecipientKey('');
		setAdditionalRecipientKeys([]);
		setSuggestions([]);
		setTemplateKey(DEFAULT_TEMPLATE.key);
		setCustomParams({ ...DEFAULT_TEMPLATE.defaultParams });
		setStartsOn(schedule.startsOn);
		setEndsOn(schedule.endsOn);
		setPreviewTitle('');
		setTitleLocked(true);
		setPersonalNote('');
		setJoinAsWell(false);
		setError('');
	}

	useEffect(() => {
		if (!isOpen || !user?.id || !endpoints?.get_user_fitness_profiles_endpoint) return;
		fetchUserFitnessProfiles({
			userId: user.id,
			endpoint: endpoints.get_user_fitness_profiles_endpoint,
			csrfToken
		})
			.then(setProfiles)
			.catch(() => setProfiles([]));
	}, [isOpen, user?.id, endpoints, csrfToken]);

	useEffect(() => {
		if (!isOpen || !user?.id || !endpoints?.get_user_household_links_endpoint) return;

		let cancelled = false;

		async function loadLinkedProfiles() {
			try {
				const linksData = await fetchHouseholdLinks({
					userId: user.id,
					endpoint: endpoints.get_user_household_links_endpoint,
					csrfToken
				});
				const linked = linksData.linked || [];
				const profileGroups = await Promise.all(
					linked.map((row) =>
						fetchLinkedHouseholdProfiles({
							userId: user.id,
							endpoint: endpoints.get_user_household_link_challengeable_profiles_endpoint,
							csrfToken,
							id: row.id
						}).catch(() => [])
					)
				);
				if (!cancelled) {
					setLinkedProfiles(profileGroups.flat());
				}
			} catch {
				if (!cancelled) setLinkedProfiles([]);
			}
		}

		loadLinkedProfiles();
		return () => {
			cancelled = true;
		};
	}, [isOpen, user?.id, endpoints, csrfToken]);

	useEffect(() => {
		if (!isOpen) return;
		if (initialRecipientKey) {
			setRecipientKey(initialRecipientKey);
		}
	}, [isOpen, initialRecipientKey]);

	useEffect(() => {
		if (!isOpen) resetForm();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		loadSuggestions();
	}, [isOpen, recipientKey, user?.id]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose();
			}
		}
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onClose]);

	function handleRecipientChange(nextKey) {
		setRecipientKey(nextKey);
		setJoinAsWell(false);
		if (!nextKey) {
			setAdditionalRecipientKeys([]);
			return;
		}
		setAdditionalRecipientKeys((current) =>
			current.filter((key) => key && key !== nextKey)
		);
	}

	function handleAdditionalRecipientChange(index, nextKey) {
		setAdditionalRecipientKeys((current) =>
			current.map((key, currentIndex) => (currentIndex === index ? nextKey : key))
		);
	}

	function handleAddRecipient() {
		if (!recipientKey) return;
		setAdditionalRecipientKeys((current) => [...current, '']);
	}

	function handleRemoveAdditionalRecipient(index) {
		setAdditionalRecipientKeys((current) =>
			current.filter((_, currentIndex) => currentIndex !== index)
		);
	}

	function recipientCanSubmit(key) {
		const { type, id } = parseRecipientKey(key);
		if (type === 'friend') return Boolean(id);
		if (type === 'profile') {
			return Boolean(loggedInIdentity.profileId && id);
		}
		return false;
	}

	async function sendChallengeToRecipient(key, challengeBody, attachJoin) {
		const { type, id } = parseRecipientKey(key);
		const body = {
			...challengeBody,
			...(attachJoin ? { also_join: true } : {})
		};

		if (type === 'friend') {
			await createFriendChallenge({
				userId: user.id,
				endpoint: endpoints.create_user_fitness_challenge_endpoint,
				csrfToken,
				payload: { challenged_id: Number(id), ...body }
			});
			return;
		}

		if (type === 'profile') {
			if (!loggedInIdentity.profileId) {
				throw new Error('Set up a primary fitness profile to send household challenges.');
			}
			await createHouseholdChallenge({
				userId: user.id,
				endpoint: endpoints.create_user_fitness_profile_challenge_endpoint,
				csrfToken,
				payload: {
					challenger_fitness_profile_id: Number(loggedInIdentity.profileId),
					challenged_fitness_profile_id: Number(id),
					...body
				}
			});
			return;
		}

		throw new Error('Choose who to challenge.');
	}

	async function loadSuggestions() {
		try {
			if (isFriend && recipientId && endpoints?.get_friend_challenge_suggestions_endpoint) {
				const data = await fetchFriendSuggestions({
					userId: user.id,
					friendUserId: recipientId,
					endpoint: endpoints.get_friend_challenge_suggestions_endpoint,
					csrfToken
				});
				setSuggestions(data.suggestions || []);
				setSuggestionMeta(data);
			} else if (isHousehold && toProfileId && endpoints?.get_fitness_profile_challenge_suggestions_endpoint) {
				const data = await fetchProfileSuggestions({
					userId: user.id,
					fitnessProfileId: toProfileId,
					endpoint: endpoints.get_fitness_profile_challenge_suggestions_endpoint,
					csrfToken
				});
				setSuggestions(data.suggestions || []);
				setSuggestionMeta(data);
			} else {
				setSuggestions([]);
			}
		} catch {
			setSuggestions([]);
		}
	}

	function applySuggestion(s) {
		const payload = buildChallengePayloadFromSuggestion(s);
		const template = findTemplate(payload.template_key);
		const params = { ...template.defaultParams, ...payload.custom_params };
		const days = durationDaysForTemplate(template, params);
		const start = todayDateInput();

		setTemplateKey(template.key);
		setCustomParams(params);
		setPreviewTitle(s.title);
		setTitleLocked(true);
		setStartsOn(start);
		setEndsOn(addDaysToDateInput(start, days));
	}

	async function handleSubmit(e) {
		e.preventDefault();
		setError('');

		if (startsOn && endsOn && endsOn < startsOn) {
			setError('End date must be on or after start date.');
			return;
		}

		const normalizedEndsOn = clampChallengeEndDate(startsOn, endsOn);
		const durationDays = daysBetweenInclusive(startsOn, normalizedEndsOn);
		if (durationDays > MAX_CHALLENGE_DURATION_DAYS) {
			setError(`Challenges can run up to ${MAX_CHALLENGE_DURATION_DAYS} days (3 months).`);
			return;
		}

		const normalizedCustomParams = { ...customParams };
		if (normalizedCustomParams.days != null) {
			normalizedCustomParams.days = clampChallengeDurationDays(normalizedCustomParams.days);
		}

		setLoading(true);
		try {
			const challengeBody = {
				template_key: templateKey,
				custom_params: normalizedCustomParams,
				personal_note: personalNote || null,
				title: previewTitle,
				starts_on: startsOn,
				ends_on: normalizedEndsOn
			};

			const uniqueKeys = [...new Set(selectedRecipientKeys)];
			if (uniqueKeys.length === 0) {
				throw new Error('Choose who to challenge.');
			}

			for (const key of uniqueKeys) {
				const attachJoin = joinAsWell && !includesSelfRecipient && !isSelfRecipientKey(key);
				await sendChallengeToRecipient(key, challengeBody, attachJoin);
			}
			onSent?.();
			onClose();
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}

	if (!isOpen) return null;

	const hasEmptyAdditionalRecipient = additionalRecipientKeys.some((key) => !key);
	const remainingRecipientCount = flattenedRecipientOptions.filter(
		(opt) => !selectedRecipientKeys.includes(opt.key)
	).length;
	const canAddRecipient =
		Boolean(recipientKey) && !hasEmptyAdditionalRecipient && remainingRecipientCount > 0;

	const canSubmit =
		selectedRecipientKeys.length > 0 &&
		!hasEmptyAdditionalRecipient &&
		previewTitle.trim() &&
		startsOn &&
		endsOn &&
		selectedRecipientKeys.every(recipientCanSubmit);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
			style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
			role="presentation"
		>
			<div
				ref={modalRef}
				className="relative flex flex-col w-full min-w-0 max-w-xl bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-lg overflow-hidden shadow-xl"
				style={{ maxHeight: '90vh' }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="send-challenge-title"
			>
				<div className="relative shrink-0 px-4 sm:px-6 pt-5 pb-4 pr-12 sm:pr-14 border-b border-gray-200 dark:border-dark-border">
					<h3 id="send-challenge-title" className="text-xl font-bold text-gray-900 dark:text-dark-text">
						New challenge
					</h3>
					<button
						type="button"
						className="absolute top-3 right-2 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition duration-300"
						onClick={onClose}
						aria-label="Close"
					>
						<svg
							className="h-6 w-6 text-gray-500 dark:text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
					<div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto min-h-0 px-4 sm:px-6 py-4 space-y-4">
					<div className="block text-sm">
						<label htmlFor="challenge-recipient-primary" className={fieldLabelClass}>
							Challenge who?
						</label>
						<RecipientSelect
							id="challenge-recipient-primary"
							value={recipientKey}
							onChange={handleRecipientChange}
							options={filterRecipientOptions(
								recipientOptions,
								selectedRecipientKeys,
								recipientKey
							)}
							placeholder="Select yourself, a household profile, or a linked household member"
							required
						/>
						{isSelfRecipientKey(recipientKey) && (
							<p className={`${hintClass} mt-1.5`}>This is a self challenge</p>
						)}
						{additionalRecipientKeys.map((key, index) => {
							const extraOptions = filterRecipientOptions(
								recipientOptions,
								selectedRecipientKeys,
								key
							);
							return (
								<div key={`additional-recipient-${index}`} className="mt-2">
									<div className="flex items-center gap-2">
										<div className="min-w-0 flex-1">
											<RecipientSelect
												id={`challenge-recipient-additional-${index}`}
												value={key}
												onChange={(nextKey) =>
													handleAdditionalRecipientChange(index, nextKey)
												}
												options={extraOptions}
												placeholder="Select another person"
												required
												selectClassName={inputClass}
											/>
										</div>
										<button
											type="button"
											onClick={() => handleRemoveAdditionalRecipient(index)}
											className="shrink-0 px-2 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-dark-text"
											aria-label="Remove person"
										>
											Remove
										</button>
									</div>
									{isSelfRecipientKey(key) && (
										<p className={`${hintClass} mt-1.5`}>This is a self challenge</p>
									)}
								</div>
							);
						})}
						{canAddRecipient && (
							<button
								type="button"
								onClick={handleAddRecipient}
								className="mt-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
							>
								+ Add
							</button>
						)}
					</div>

					{recipientKey && (
						<>
							{suggestions.length > 0 && (
								<div>
									<p className="text-sm font-medium text-gray-900 dark:text-dark-text">Suggested for {suggestName}</p>
									<p className={`${hintClass} mt-0.5`}>
										Based on habits and goals from their Fantasy Fitness profile.
									</p>
									<div className="mt-2 flex gap-2 overflow-x-auto pb-1">
										{suggestions.map((s) => (
											<button
												key={`${s.template_key}-${s.title}`}
												type="button"
												onClick={() => applySuggestion(s)}
												className={suggestionChipClass}
											>
												<div className={suggestionChipTitleClass}>{s.title}</div>
												<div className={suggestionChipReasonClass}>{s.reason}</div>
											</button>
										))}
									</div>
								</div>
							)}

							<ChallengeTemplateConfigurator
								templateKey={templateKey}
								customParams={customParams}
								onTemplateKeyChange={setTemplateKey}
								onCustomParamsChange={setCustomParams}
								startsOn={startsOn}
								endsOn={endsOn}
								onStartsOnChange={setStartsOn}
								onEndsOnChange={setEndsOn}
								title={previewTitle}
								onTitleChange={setPreviewTitle}
								titleLocked={titleLocked}
								onTitleLockedChange={setTitleLocked}
							/>

							<label className="block text-sm">
								<span className={fieldLabelClass}>Note (optional)</span>
								<textarea
									className={`${inputClass} mt-1`}
									maxLength={200}
									rows={2}
									value={personalNote}
									onChange={(e) => setPersonalNote(e.target.value)}
									placeholder="Words of encouragement or maybe a little trash talk..."
								/>
							</label>

							{!includesSelfRecipient && (
								<label className="flex items-start gap-3 cursor-pointer text-sm">
									<input
										type="checkbox"
										checked={joinAsWell}
										onChange={(e) => setJoinAsWell(e.target.checked)}
										className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
									/>
									<span className="text-gray-700 dark:text-dark-text-secondary">
										I'm going to do this challenge as well
									</span>
								</label>
							)}

							<ChallengeSendSummary
								challengerName={summaryParties?.challenger}
								challengedName={summaryParties?.challenged}
								challengedNames={summaryParties?.challengedNames}
								title={
									previewTitle.trim() ||
									(isCustomTemplate(templateKey)
										? ''
										: buildChallengeTitle(templateKey, customParams))
								}
								startsOn={startsOn}
								endsOn={endsOn}
								personalNote={personalNote}
								alsoJoin={Boolean(summaryParties?.alsoJoin)}
							/>
						</>
					)}

					</div>

					<div className="shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-dark-border space-y-3">
						{error && (
							<p className="text-sm text-red-600 dark:text-red-400" role="alert">
								{error}
							</p>
						)}
						<div className="flex flex-wrap justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							disabled={loading}
							className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={loading || !canSubmit}
							className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-semibold disabled:opacity-50"
						>
							{loading
								? 'Sending…'
								: selectedRecipientKeys.length > 1
									? 'Send challenges'
									: 'Send challenge'}
						</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
}
